import { Alert, Button, Text, Tooltip } from '@hotjar/hotsauce';
import { useVideoService } from '@unmoderated/video-service';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { WithErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary';
import { FullScreen } from '../../components/FullScreen/FullScreen';
import { SystemCheck } from '../../components/UnmoderatedTest/SystemCheck';
import { VideoFeeds } from '../../components/UnmoderatedTest/VideoFeeds';
import { routes } from '../../routes';
import { useObserveDailyEvents } from '../../utils/hooks/useObserveDailyEvents';
import { getAnalytics } from '../../utils/observability';
import { Preview, Settings } from './UnmoderatedScreen.styles';
import { useProjectParams } from '../../utils/hooks/useProjectParams';

export function UnmoderatedScreenComponent() {
  useObserveDailyEvents();

  const analytics = getAnalytics();
  const navigate = useNavigate();
  const { t } = useTranslation('user-test', { keyPrefix: 'system-check' });

  const { currentCam, currentMic, isSharingScreen, sharedSurface } = useVideoService();

  const navigateToPreview = () => {
    const camState = `cam_${currentCam ? 'yes' : 'no'}`;
    const micState = `mic_${currentMic ? 'yes' : 'no'}`;
    const screenState = `screen_${isSharingScreen ? 'yes' : 'no'}`;
    analytics.track('Element Clicked', {
      element_name: 'Set Up Next Button',
      ui_position: `ut_lobby.setup_screen.${camState}.${micState}.${screenState}`,
    });
    navigate(routes.PREVIEW.getPath(window.location.search));
  };

  useEffect(() => {
    if (isSharingScreen && sharedSurface !== 'window') {
      navigate(routes.PRE_CHECK.getPath(window.location.search));
    }
  }, [sharedSurface, navigate, isSharingScreen]);

  const { getParam } = useProjectParams();
  const isPreview = !!getParam('is_preview');

  const canStartTest = (isSharingScreen && sharedSurface !== 'browser' && currentMic) || isPreview;

  return (
    <FullScreen
      leftSide={
        <Settings direction="column">
          <Text textType="header2" as="h2">
            {t('title')}
          </Text>
          <Text>{t('description')}</Text>
          <SystemCheck />
        </Settings>
      }
      hideOnSmallScreen={!isSharingScreen ? 'right' : undefined}
      rightSide={
        <Preview direction="column">
          <VideoFeeds isScreenShareOn={isSharingScreen} />

          {isSharingScreen && sharedSurface === 'browser' && (
            <Alert kind="danger">{t('alerts.screens-share-tab')}</Alert>
          )}

          {isSharingScreen && sharedSurface === 'monitor' && (
            <Alert kind="warning">{t('alerts.screens-share-screen')}</Alert>
          )}

          {isSharingScreen && sharedSurface === 'unknown' && (
            <Alert kind="warning">{t('alerts.screens-share-unknown')}</Alert>
          )}
        </Preview>
      }
      footer={
        <Tooltip isDisabled={!!canStartTest} text={t('disabled-tooltip')}>
          <Button isDisabled={!canStartTest} onClick={navigateToPreview} className="start-button">
            Next
          </Button>
        </Tooltip>
      }
    />
  );
}

export const UnmoderatedScreen = WithErrorBoundary(UnmoderatedScreenComponent, 'UnmoderatedScreen');
